import { FunctionComponent, useState } from "react";

import {
  ConfirmDialog,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { dangerButtonStyles } from "@components/buttons/DangerButton";
import { useUnlinkTenantFromBpId } from "@libs/api/gateways/bp-id/bp-id.hooks";

export interface UnlinkTenantButtonProps {
  bpIdUserId: string;
  tenantId: string;
  tenantUserId: string;
  tenantName?: string;
  // True when this is the only Premier (federated) tenant linked to the account.
  // Note this ignores CAM / Omni cloud tenants, which unlinking does not affect.
  isLastFederatedTenant: boolean;
}

export const UnlinkTenantButton: FunctionComponent<UnlinkTenantButtonProps> = ({
  bpIdUserId,
  tenantId,
  tenantUserId,
  tenantName,
  isLastFederatedTenant
}) => {
  const theme = useTheme();
  const [hidden, setHidden] = useState(true);
  const { mutateAsync: unlink, isLoading, error } = useUnlinkTenantFromBpId(
    bpIdUserId
  );

  const onConfirm = async () => {
    await unlink({ tenantId, tenantUserId });
    setHidden(true);
  };

  return (
    <>
      <DefaultButton
        iconProps={{ iconName: "PlugDisconnected" }}
        onClick={() => setHidden(false)}
      >
        Unlink
      </DefaultButton>
      <ConfirmDialog
        hidden={hidden}
        dialogContentProps={{ title: "Unlink tenant from Bp Id account" }}
        confirmButtonProps={{
          text: "Unlink",
          styles: dangerButtonStyles(theme),
          disabled: isLoading
        }}
        cancelButtonProps={{ text: "Cancel" }}
        confirming={isLoading}
        onConfirm={onConfirm}
        onCancel={() => setHidden(true)}
      >
        <Stack tokens={{ childrenGap: theme.spacing.s2 }}>
          <Text>
            Unlinking {tenantName ?? "this tenant"} means the user will be
            unable to access Bp applications for this tenant.
          </Text>
          {isLastFederatedTenant && (
            <MessageBar messageBarType={MessageBarType.warning}>
              This is the last Premier (federated) tenant linked to this
              account. After unlinking, the user will have no Premier
              (federated) login and would need to be re-invited to a Premier
              tenant. Any CAM / Omni cloud tenant access is unaffected.
            </MessageBar>
          )}
          {error && (
            <MessageBar messageBarType={MessageBarType.error}>
              {error.message}
            </MessageBar>
          )}
        </Stack>
      </ConfirmDialog>
    </>
  );
};
